import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertCircle,
  ArrowLeft,
  Captions,
  Check,
  ChevronRight,
  Clock3,
  ListVideo,
  LoaderCircle,
  Maximize2,
  Minimize2,
  Pause,
  PictureInPicture2,
  Play,
  RotateCcw,
  RotateCw,
  Settings2,
  SkipBack,
  SkipForward,
  Volume1,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react'
import { repository } from '../services/repository'
import { useAppStore } from '../store/app-store'
import type { VideoSubtitleTrack } from '../types'

export interface VideoLectureItem {
  id: string
  title: string
  duration: string
  durationSeconds?: number
  subject: string
  videoUrl: string
  sizeBytes?: number
  subtitleTracks?: VideoSubtitleTrack[]
}

interface VideoPlayerModalProps {
  lesson: VideoLectureItem
  playlist: VideoLectureItem[]
  libraryName: string
  libraryIcon: string
  folderName: string
  onClose: () => void
  onSelectLesson: (lesson: VideoLectureItem) => void
}

interface SavedVideoPosition {
  currentTime: number
  duration: number
  percent: number
}

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
const EMPTY_SUBTITLE_TRACKS: VideoSubtitleTrack[] = []

interface PlaybackPreferences {
  volume: number
  muted: boolean
  rate: number
  captions: boolean
  autoplay: boolean
  captionSize: number
}

const DEFAULT_PREFERENCES: PlaybackPreferences = { volume: 1, muted: false, rate: 1, captions: true, autoplay: true, captionSize: 100 }

function preferencesKey(userId?: string): string { return `imed.video.preferences.${userId || 'guest'}` }

function readPreferences(userId?: string): PlaybackPreferences {
  try {
    const saved = JSON.parse(localStorage.getItem(preferencesKey(userId)) || '{}') as Partial<PlaybackPreferences>
    return {
      volume: typeof saved.volume === 'number' && saved.volume >= 0 && saved.volume <= 1 ? saved.volume : 1,
      muted: typeof saved.muted === 'boolean' ? saved.muted : false,
      rate: SPEEDS.includes(saved.rate ?? 0) ? saved.rate! : 1,
      captions: typeof saved.captions === 'boolean' ? saved.captions : true,
      autoplay: typeof saved.autoplay === 'boolean' ? saved.autoplay : true,
      captionSize: [80, 100, 125, 150].includes(saved.captionSize ?? 0) ? saved.captionSize! : 100,
    }
  } catch { return DEFAULT_PREFERENCES }
}

function subtitleVtt(source: string): string | null {
  const clean = source.replace(/^\uFEFF/, '').trim().replace(/\r\n?/g, '\n')
  if (/^WEBVTT(?:\s|$)/.test(clean)) return clean
  if (/\d{2}:\d{2}:\d{2},\d{3}\s+-->\s+\d{2}:\d{2}:\d{2},\d{3}/.test(clean)) {
    return `WEBVTT\n\n${clean.replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2')}`
  }
  return null
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const total = Math.floor(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const remainingSeconds = total % 60
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
    : `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

function progressStorageKey(lessonId: string, userId?: string): string {
  return `imed.video.resume.${userId ? `${encodeURIComponent(userId)}.` : ''}${lessonId}`
}

function readSavedPosition(lessonId: string, userId?: string): SavedVideoPosition | null {
  try {
    const raw = localStorage.getItem(progressStorageKey(lessonId, userId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as SavedVideoPosition
    return Number.isFinite(parsed.currentTime) && parsed.currentTime >= 0 && Number.isFinite(parsed.duration) && parsed.duration > 0
      ? { ...parsed, percent: Math.min(100, (parsed.currentTime / parsed.duration) * 100) } : null
  } catch {
    return null
  }
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(target.tagName)
}

export function VideoPlayerModal({
  lesson,
  playlist,
  libraryName,
  libraryIcon,
  folderName,
  onClose,
  onSelectLesson,
}: VideoPlayerModalProps) {
  const userId = useAppStore((state) => state.user?.id)
  const rootRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSavedSecondRef = useRef(-1)
  const resumePositionRef = useRef(0)
  const shouldAutoPlayRef = useRef(false)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const interactedRef = useRef(false)
  const preferences = useMemo(() => readPreferences(userId), [userId])

  const tracks = lesson.subtitleTracks ?? EMPTY_SUBTITLE_TRACKS
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [mediaError, setMediaError] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(lesson.durationSeconds ?? 0)
  const [buffered, setBuffered] = useState(0)
  const [volume, setVolume] = useState(preferences.volume)
  const [muted, setMuted] = useState(preferences.muted)
  const [playbackRate, setPlaybackRate] = useState(preferences.rate)
  const [captionsEnabled, setCaptionsEnabled] = useState(preferences.captions)
  const [autoplay, setAutoplay] = useState(preferences.autoplay)
  const [captionSize, setCaptionSize] = useState(preferences.captionSize)
  const [notice, setNotice] = useState<string | null>(null)
  const [subtitleLoading, setSubtitleLoading] = useState(false)
  const [activeTrackId, setActiveTrackId] = useState(tracks[0]?.id ?? '')
  const [subtitleSources, setSubtitleSources] = useState<Record<string, string>>({})
  const [playlistOpen, setPlaylistOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [controlsVisible, setControlsVisible] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isPictureInPicture, setIsPictureInPicture] = useState(false)

  const activeIndex = useMemo(() => playlist.findIndex((item) => item.id === lesson.id), [lesson.id, playlist])
  const canGoPrevious = activeIndex > 0
  const canGoNext = activeIndex >= 0 && activeIndex < playlist.length - 1
  const playedPercent = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0
  const activeTrack = tracks.find((track) => track.id === activeTrackId) ?? tracks[0]
  const playableTracks = useMemo(() => tracks.filter((track) => Boolean(subtitleSources[track.id])), [tracks, subtitleSources])

  const clearControlsTimer = useCallback(() => {
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current)
    controlsTimerRef.current = null
  }, [])

  const showControls = useCallback(() => {
    clearControlsTimer()
    setControlsVisible(true)
    if (videoRef.current && !videoRef.current.paused && !settingsOpen && !playlistOpen) {
      controlsTimerRef.current = setTimeout(() => {
        if (!rootRef.current?.querySelector('.imed-player-header :focus-visible, .imed-player-controls :focus-visible')) setControlsVisible(false)
      }, 3200)
    }
  }, [clearControlsTimer, playlistOpen, settingsOpen])

  const persistProgress = useCallback((forceComplete = false, media = videoRef.current) => {
    if (!media || !Number.isFinite(media.duration) || media.duration <= 0) return
    const nextTime = forceComplete ? media.duration : Math.max(0, Math.min(media.duration, media.currentTime))
    const percent = forceComplete ? 100 : Math.min(100, (nextTime / media.duration) * 100)
    const saved = { currentTime: nextTime, duration: media.duration, percent }
    let localSaved = false
    try { localStorage.setItem(progressStorageKey(lesson.id, userId), JSON.stringify(saved)); localSaved = true } catch { /* Account storage may still work. */ }
    if (userId) {
      void repository.saveProgress({
        userId,
        entityId: lesson.id,
        entityType: 'video',
        percent,
        currentTime: nextTime,
        duration: media.duration,
      }).catch(() => setNotice(localSaved ? 'Your position is saved locally; account progress is temporarily unavailable.' : 'Your position could not be saved. Device storage may be full.'))
    }
    else if (!localSaved) setNotice('Your position could not be saved. Device storage may be full.')
  }, [lesson.id, userId])

  const selectLesson = useCallback((nextLesson: VideoLectureItem) => {
    if (nextLesson.id === lesson.id) { setPlaylistOpen(false); return }
    shouldAutoPlayRef.current = Boolean(videoRef.current && !videoRef.current.paused)
    persistProgress()
    onSelectLesson(nextLesson)
  }, [lesson.id, onSelectLesson, persistProgress])

  const togglePlay = useCallback(async () => {
    const media = videoRef.current
    if (!media || mediaError) return
    interactedRef.current = true
    if (media.paused) {
      try { await media.play() } catch (error) {
        if (videoRef.current !== media || (error instanceof DOMException && error.name === 'AbortError')) return
        setNotice('Playback did not start. Press Play to try again, or reload the lecture if the issue continues.')
      }
    } else {
      media.pause()
    }
  }, [mediaError])

  const seekBy = useCallback((seconds: number) => {
    const media = videoRef.current
    if (!media || !Number.isFinite(media.duration)) return
    interactedRef.current = true
    media.currentTime = Math.max(0, Math.min(media.duration, media.currentTime + seconds))
    setCurrentTime(media.currentTime)
    showControls()
  }, [showControls])

  const changeVolume = useCallback((nextVolume: number) => {
    const clamped = Math.max(0, Math.min(1, nextVolume))
    setVolume(clamped)
    setMuted(clamped === 0)
    if (videoRef.current) {
      videoRef.current.volume = clamped
      videoRef.current.muted = clamped === 0
    }
  }, [])

  const toggleMute = useCallback(() => {
    const media = videoRef.current
    if (!media) return
    const next = !media.muted && media.volume > 0
    if (!next && media.volume === 0) { media.volume = 0.5; setVolume(0.5) }
    media.muted = next
    setMuted(next)
  }, [])

  const applyCaptions = useCallback(() => {
    const media = videoRef.current
    if (!media) return
    Array.from(media.textTracks).forEach((track, index) => {
      track.mode = captionsEnabled && playableTracks[index]?.id === activeTrack?.id ? 'showing' : 'disabled'
    })
  }, [activeTrack?.id, captionsEnabled, playableTracks])

  const toggleFullscreen = useCallback(async () => {
    const root = rootRef.current
    if (!root) return
    try {
      if (document.fullscreenElement) await document.exitFullscreen()
      else await root.requestFullscreen()
    } catch {
      setNotice('Fullscreen is not available in this window. You can continue watching here.')
    }
  }, [])

  const togglePictureInPicture = useCallback(async () => {
    const media = videoRef.current
    if (!media || !document.pictureInPictureEnabled) return
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture()
      else await media.requestPictureInPicture()
    } catch {
      setNotice('Picture-in-picture is not available for this lecture. You can continue watching here.')
    }
  }, [])

  const closePlayer = useCallback(() => {
    persistProgress()
    videoRef.current?.pause()
    if (document.fullscreenElement === rootRef.current) void document.exitFullscreen().catch(() => undefined)
    if (document.pictureInPictureElement === videoRef.current) void document.exitPictureInPicture().catch(() => undefined)
    onClose()
  }, [onClose, persistProgress])

  useEffect(() => {
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    rootRef.current?.focus()
    return () => {
      document.body.style.overflow = previousOverflow
      previousFocusRef.current?.focus()
      clearControlsTimer()
    }
  }, [clearControlsTimer])

  useEffect(() => {
    try { localStorage.setItem(preferencesKey(userId), JSON.stringify({ volume, muted, rate: playbackRate, captions: captionsEnabled, autoplay, captionSize })) } catch { /* Preferences are optional when storage is unavailable. */ }
  }, [autoplay, captionSize, captionsEnabled, muted, playbackRate, userId, volume])

  useEffect(() => {
    const media = videoRef.current
    if (!media) return
    media.volume = volume
    media.muted = muted
    media.playbackRate = playbackRate
  }, [lesson.id, lesson.videoUrl, volume, muted, playbackRate])

  useEffect(() => {
    // Capture this element: a later lesson replaces the ref before cleanup runs.
    const media = videoRef.current
    const save = () => persistProgress(false, media)
    const onVisibilityChange = () => { if (document.visibilityState === 'hidden') save() }
    window.addEventListener('pagehide', save)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      save()
      window.removeEventListener('pagehide', save)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [lesson.videoUrl, persistProgress])

  useEffect(() => {
    const saved = readSavedPosition(lesson.id, userId)
    resumePositionRef.current = saved && saved.percent < 95 ? saved.currentTime : 0
    lastSavedSecondRef.current = -1
    interactedRef.current = false
    setCurrentTime(0)
    setDuration(lesson.durationSeconds ?? 0)
    setBuffered(0)
    setIsPlaying(false)
    setIsLoading(true)
    setMediaError(null)
    setNotice(null)
    setActiveTrackId(tracks[0]?.id ?? '')
    setSettingsOpen(false)

    let active = true
    if (userId) {
      void repository.progress(userId).then((items) => {
        if (!active) return
        const savedProgress = items.find((item) => item.entityId === lesson.id && item.entityType === 'video')
        if (savedProgress?.currentTime && savedProgress.percent < 95) {
          resumePositionRef.current = savedProgress.currentTime
          const media = videoRef.current
          if (!interactedRef.current && media?.readyState && media.currentTime < 1 && savedProgress.currentTime < media.duration - 5) {
            media.currentTime = savedProgress.currentTime
            setCurrentTime(savedProgress.currentTime)
            setNotice(`Resumed at ${formatTime(savedProgress.currentTime)}`)
          }
        }
      }).catch(() => undefined)
    }
    return () => { active = false }
  }, [lesson.durationSeconds, lesson.id, lesson.videoUrl, tracks, userId])

  useEffect(() => {
    const controller = new AbortController()
    const objectUrls: string[] = []
    let mounted = true

    setSubtitleSources({})
    setSubtitleLoading(tracks.length > 0)
    void Promise.all(tracks.map(async (track) => {
      try {
        const response = await fetch(track.url, { signal: controller.signal })
        if (!response.ok) return null
        const subtitleText = subtitleVtt(await response.text())
        if (!mounted || !subtitleText) return null
        const objectUrl = URL.createObjectURL(new Blob([subtitleText], { type: 'text/vtt' }))
        objectUrls.push(objectUrl)
        return [track.id, objectUrl] as const
      } catch {
        return null
      }
    })).then((loadedTracks) => {
      if (!mounted) return
      setSubtitleSources(Object.fromEntries(loadedTracks.filter((track): track is readonly [string, string] => Boolean(track))))
      setSubtitleLoading(false)
    })

    return () => {
      mounted = false
      controller.abort()
      objectUrls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [lesson.id, tracks])

  useEffect(() => {
    applyCaptions()
  }, [applyCaptions])

  useEffect(() => {
    if (settingsOpen || playlistOpen) {
      clearControlsTimer()
      setControlsVisible(true)
    }
  }, [clearControlsTimer, playlistOpen, settingsOpen])

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(document.fullscreenElement === rootRef.current)
    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
  }, [])

  useEffect(() => {
    const media = videoRef.current
    if (!media) return
    const onEnter = () => setIsPictureInPicture(true)
    const onLeave = () => setIsPictureInPicture(false)
    media.addEventListener('enterpictureinpicture', onEnter)
    media.addEventListener('leavepictureinpicture', onLeave)
    return () => {
      media.removeEventListener('enterpictureinpicture', onEnter)
      media.removeEventListener('leavepictureinpicture', onLeave)
    }
  }, [lesson.id])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (settingsOpen) setSettingsOpen(false)
        else if (playlistOpen) setPlaylistOpen(false)
        else if (!document.fullscreenElement) closePlayer()
        return
      }
      if (event.key === 'Tab' && rootRef.current) {
        showControls()
        const focusable = Array.from(rootRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]'))
          .filter((element) => getComputedStyle(element).display !== 'none' && getComputedStyle(element).visibility !== 'hidden')
        if (focusable.length) {
          const first = focusable[0]
          const last = focusable[focusable.length - 1]
          if (event.shiftKey && (document.activeElement === first || document.activeElement === rootRef.current)) { event.preventDefault(); last.focus() }
          else if (!event.shiftKey && (document.activeElement === last || document.activeElement === rootRef.current)) { event.preventDefault(); first.focus() }
        }
        return
      }
      if (isEditableTarget(event.target) || event.repeat || event.altKey || event.ctrlKey || event.metaKey) return
      const key = event.key.toLowerCase()
      if (key === ' ' || key === 'k') { event.preventDefault(); void togglePlay() }
      else if (event.key === 'ArrowLeft' || key === 'j') { event.preventDefault(); seekBy(-10) }
      else if (event.key === 'ArrowRight' || key === 'l') { event.preventDefault(); seekBy(10) }
      else if (event.key === 'ArrowUp') { event.preventDefault(); changeVolume(volume + 0.05) }
      else if (event.key === 'ArrowDown') { event.preventDefault(); changeVolume(volume - 0.05) }
      else if (key === 'm') toggleMute()
      else if (key === 'c' && tracks.length) setCaptionsEnabled((enabled) => !enabled)
      else if (key === 'f') void toggleFullscreen()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [changeVolume, closePlayer, playlistOpen, seekBy, settingsOpen, showControls, toggleFullscreen, toggleMute, togglePlay, tracks.length, volume])

  const playAdjacent = (offset: number) => {
    const next = playlist[activeIndex + offset]
    if (next) selectLesson(next)
  }

  return (
    <div
      ref={rootRef}
      className={`imed-video-player ${controlsVisible || !isPlaying ? 'controls-visible' : 'controls-hidden'} ${playlistOpen ? 'playlist-visible' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={`Playing ${lesson.title}`}
      tabIndex={-1}
      onMouseMove={showControls}
      onFocusCapture={showControls}
    >
      <div className="imed-player-stage" onClick={() => {
        if (!isPlaying) { void togglePlay(); return }
        clearControlsTimer()
        if (controlsVisible) { rootRef.current?.focus({ preventScroll: true }); clearControlsTimer(); setControlsVisible(false) }
        else showControls()
      }} onDoubleClick={() => void toggleFullscreen()}>
        <video
          ref={videoRef}
          key={`${lesson.id}:${lesson.videoUrl}`}
          className="imed-player-video"
          src={lesson.videoUrl}
          playsInline
          preload="metadata"
          style={{ fontSize: `${20 * captionSize / 100}px` }}
          onLoadStart={() => { setIsLoading(true); setMediaError(null) }}
          onLoadedMetadata={(event) => {
            const media = event.currentTarget
            media.volume = volume; media.muted = muted; media.playbackRate = playbackRate
            setDuration(Number.isFinite(media.duration) ? media.duration : 0)
            const resumeAt = resumePositionRef.current
            if (resumeAt > 0 && resumeAt < media.duration - 5) {
              media.currentTime = resumeAt
              setCurrentTime(resumeAt)
              setNotice(`Resumed at ${formatTime(resumeAt)}`)
            }
            applyCaptions()
            if (shouldAutoPlayRef.current) {
              shouldAutoPlayRef.current = false
              void media.play().catch(() => undefined)
            }
          }}
          onCanPlay={() => setIsLoading(false)}
          onWaiting={() => setIsLoading(true)}
          onPlaying={() => { setIsLoading(false); setIsPlaying(true); showControls() }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => { setIsPlaying(false); setControlsVisible(true); persistProgress() }}
          onDurationChange={(event) => setDuration(Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0)}
          onProgress={(event) => {
            const media = event.currentTarget
            if (media.buffered.length && media.duration) setBuffered(Math.min(100, (media.buffered.end(media.buffered.length - 1) / media.duration) * 100))
          }}
          onTimeUpdate={(event) => {
            const media = event.currentTarget
            setCurrentTime(media.currentTime)
            const second = Math.floor(media.currentTime)
            if (second > 0 && second % 5 === 0 && second !== lastSavedSecondRef.current) {
              lastSavedSecondRef.current = second
              persistProgress()
            }
          }}
          onEnded={() => {
            setIsPlaying(false)
            persistProgress(true)
            if (autoplay && canGoNext) {
              shouldAutoPlayRef.current = true
              onSelectLesson(playlist[activeIndex + 1])
            }
          }}
          onError={() => {
            setIsLoading(false)
            setIsPlaying(false)
            setMediaError('This extracted video could not be decoded. Re-download the package or try another lecture.')
          }}
        >
          {playableTracks.map((track) => (
            <track key={track.id} kind="subtitles" src={subtitleSources[track.id]} srcLang={track.language === 'und' ? 'en' : track.language} label={track.label} default={captionsEnabled && track.id === activeTrack?.id} onLoad={applyCaptions} />
          ))}
        </video>

        {isLoading && !mediaError && <div className="imed-player-status"><LoaderCircle className="animate-spin" /><span>Loading lecture…</span></div>}
        {mediaError && (
          <div className="imed-player-error" role="alert" onClick={(event) => event.stopPropagation()}>
            <AlertCircle />
            <strong>Unable to play this lecture</strong>
            <p>{mediaError}</p>
            <button type="button" onClick={() => { setMediaError(null); setIsLoading(true); videoRef.current?.load() }}>Reload video</button>
          </div>
        )}
        {!isPlaying && !isLoading && !mediaError && (
          <button type="button" className="imed-player-center-play" onClick={(event) => { event.stopPropagation(); void togglePlay() }} aria-label="Play video"><Play fill="currentColor" /></button>
        )}
      </div>

      <header className="imed-player-header" onClick={(event) => event.stopPropagation()}>
        <div className="imed-player-heading">
          <button type="button" className="imed-player-icon-button" onClick={closePlayer} aria-label="Close video player" title="Back to lectures"><ArrowLeft /></button>
          <img src={libraryIcon} alt="" className="imed-player-library-icon" />
          <div>
            <h2>{lesson.title}</h2>
            <p><span>{libraryName}</span><i />{folderName}<i />{lesson.subject}</p>
          </div>
        </div>
        <div className="imed-player-header-actions">
          {tracks.length > 0 && <button type="button" className={`imed-player-action ${captionsEnabled ? 'active' : ''}`} onClick={() => setCaptionsEnabled((enabled) => !enabled)} aria-pressed={captionsEnabled} title="Toggle captions (C)"><Captions /><span>CC</span></button>}
          <button type="button" className={`imed-player-action ${playlistOpen ? 'active' : ''}`} onClick={() => { setPlaylistOpen((open) => !open); setSettingsOpen(false) }} aria-pressed={playlistOpen} title="Open lecture list"><ListVideo /><span>{playlist.length}</span></button>
          <button type="button" className={`imed-player-action ${settingsOpen ? 'active' : ''}`} onClick={() => { setSettingsOpen((open) => !open); setPlaylistOpen(false) }} aria-pressed={settingsOpen} title="Playback settings"><Settings2 /></button>
          <button type="button" className="imed-player-icon-button" onClick={closePlayer} aria-label="Close video player" title="Close"><X /></button>
        </div>
      </header>
      {notice && <div className="imed-player-notice" role="status"><span>{notice}</span><button type="button" aria-label="Dismiss playback message" onClick={() => setNotice(null)}><X size={14} /></button></div>}

      <footer className="imed-player-controls" onClick={(event) => event.stopPropagation()}>
        <div className="imed-player-timeline-row">
          <span>{formatTime(currentTime)}</span>
          <input
            className="imed-player-timeline"
            type="range"
            min="0"
            max={duration || 0}
            step="0.1"
            value={Math.min(currentTime, duration || 0)}
            onChange={(event) => {
              interactedRef.current = true
              const next = Number(event.currentTarget.value)
              if (videoRef.current) videoRef.current.currentTime = next
              setCurrentTime(next)
            }}
            aria-label="Video position"
            style={{ '--played': `${playedPercent}%`, '--buffered': `${buffered}%` } as React.CSSProperties}
          />
          <span>{formatTime(Math.round(duration))}</span>
        </div>
        <div className="imed-player-transport">
          <div className="imed-player-transport-group">
            <button type="button" onClick={() => playAdjacent(-1)} disabled={!canGoPrevious} aria-label="Previous lecture" title="Previous lecture"><SkipBack /></button>
            <button type="button" onClick={() => seekBy(-10)} aria-label="Rewind 10 seconds" title="Rewind 10 seconds (J)"><RotateCcw /><small>10</small></button>
            <button type="button" className="imed-player-primary-play" onClick={() => void togglePlay()} aria-label={isPlaying ? 'Pause' : 'Play'}>{isPlaying ? <Pause fill="currentColor" /> : <Play fill="currentColor" />}</button>
            <button type="button" onClick={() => seekBy(10)} aria-label="Forward 10 seconds" title="Forward 10 seconds (L)"><RotateCw /><small>10</small></button>
            <button type="button" onClick={() => playAdjacent(1)} disabled={!canGoNext} aria-label="Next lecture" title="Next lecture"><SkipForward /></button>
          </div>
          <div className="imed-player-now-playing"><Clock3 /><span>{lesson.duration === 'Ready to play' ? formatTime(duration) : lesson.duration}</span></div>
          <div className="imed-player-transport-group right">
            <button type="button" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'} title="Mute (M)">{muted || volume === 0 ? <VolumeX /> : volume < 0.5 ? <Volume1 /> : <Volume2 />}</button>
            <input type="range" className="imed-player-volume" min="0" max="1" step="0.05" value={muted ? 0 : volume} onChange={(event) => changeVolume(Number(event.currentTarget.value))} aria-label="Volume" />
            <button type="button" className="imed-player-rate" onClick={() => setSettingsOpen((open) => !open)} aria-label="Playback speed">{playbackRate}×</button>
            {document.pictureInPictureEnabled && <button type="button" className={isPictureInPicture ? 'active' : ''} onClick={() => void togglePictureInPicture()} aria-label="Picture in picture" title="Picture in picture"><PictureInPicture2 /></button>}
            <button type="button" onClick={() => void toggleFullscreen()} aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} title="Fullscreen (F)">{isFullscreen ? <Minimize2 /> : <Maximize2 />}</button>
          </div>
        </div>
      </footer>

      {settingsOpen && (
        <section className="imed-player-settings" aria-label="Playback settings">
          <div className="imed-player-popover-heading"><strong>Playback settings</strong><button type="button" onClick={() => setSettingsOpen(false)} aria-label="Close settings"><X /></button></div>
          <label>Speed</label>
          <div className="imed-player-speed-grid">
            {SPEEDS.map((speed) => <button key={speed} type="button" className={playbackRate === speed ? 'active' : ''} onClick={() => { setPlaybackRate(speed); if (videoRef.current) videoRef.current.playbackRate = speed }}>{playbackRate === speed && <Check />} {speed}×</button>)}
          </div>
          {tracks.length > 0 && (
            <>
              <label>Subtitles</label>
              <p className="imed-player-subtitle-status">{subtitleLoading ? 'Loading subtitles…' : playableTracks.length ? `${playableTracks.length} subtitle track${playableTracks.length === 1 ? '' : 's'} available` : 'Subtitle files could not be loaded.'}</p>
              <button type="button" className={!captionsEnabled ? 'selected' : ''} onClick={() => setCaptionsEnabled(false)}><span>Off</span>{!captionsEnabled && <Check />}</button>
              {playableTracks.map((track) => <button key={track.id} type="button" className={captionsEnabled && activeTrack?.id === track.id ? 'selected' : ''} onClick={() => { setActiveTrackId(track.id); setCaptionsEnabled(true) }}><span>{track.label}</span>{captionsEnabled && activeTrack?.id === track.id && <Check />}</button>)}
              <label htmlFor="imed-caption-size">Caption size</label><select id="imed-caption-size" value={captionSize} onChange={event => setCaptionSize(Number(event.target.value))}><option value={80}>Small</option><option value={100}>Standard</option><option value={125}>Large</option><option value={150}>Extra large</option></select>
            </>
          )}
          <button type="button" role="switch" aria-checked={autoplay} className="imed-player-autoplay" onClick={() => setAutoplay(value => !value)}><span>Play next lecture automatically</span><strong>{autoplay ? 'On' : 'Off'}</strong></button>
          <p className="imed-player-shortcuts">Space · Play / pause<br />← / → · Skip 10 seconds · F · Fullscreen</p>
        </section>
      )}

      {playlistOpen && (
        <aside className="imed-player-playlist" aria-label="Lecture playlist">
          <div className="imed-player-playlist-header">
            <div><span>Course playlist</span><strong>{folderName}</strong><small>{playlist.length} lectures</small></div>
            <button type="button" onClick={() => setPlaylistOpen(false)} aria-label="Close playlist"><X /></button>
          </div>
          <div className="imed-player-playlist-scroll">
            {playlist.map((item, index) => {
              const selected = item.id === lesson.id
              const saved = readSavedPosition(item.id, userId)
              const itemProgress = selected ? playedPercent : saved?.percent ?? 0
              return (
                <button key={item.id} type="button" className={`imed-player-playlist-item ${selected ? 'selected' : ''}`} onClick={() => selectLesson(item)}>
                  <span className="imed-player-playlist-index">{selected && isPlaying ? <Play fill="currentColor" /> : String(index + 1).padStart(2, '0')}</span>
                  <span className="imed-player-playlist-copy"><strong>{item.title}</strong><small>{item.duration}{item.subtitleTracks?.length ? ' · CC' : ''}</small><i><b style={{ width: `${itemProgress}%` }} /></i></span>
                  <ChevronRight />
                </button>
              )
            })}
          </div>
        </aside>
      )}
    </div>
  )
}
